/**
 * Auto-Login Page
 * ===============
 * Lets a one-click URL log a session in by setting the `session_token`
 * cookie + localStorage Bearer-token fallback. Used to grant external
 * reviewers (e.g. Manus AI) read-only access via a single shareable link.
 *
 * URL pattern:  /auto-login?t=<SESSION_TOKEN>&r=<optional_redirect>
 */
import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Loader, ShieldCheck, AlertCircle } from 'lucide-react';

const AutoLoginPage = () => {
  const location = useLocation();
  const [status, setStatus] = useState('working'); // working | done | error
  const [redirectTo, setRedirectTo] = useState('/');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('t');
    const redirect = params.get('r') || '/';
    setRedirectTo(redirect);

    if (!token) {
      setStatus('error');
      return;
    }

    // 1) Set cookie (works on same-origin)
    try {
      const isHttps = window.location.protocol === 'https:';
      const maxAge = 60 * 60 * 24 * 7; // 7 days
      const sameSite = isHttps ? 'None' : 'Lax';
      const secure = isHttps ? '; secure' : '';
      document.cookie = `session_token=${token}; path=/; max-age=${maxAge}; samesite=${sameSite}${secure}`;
    } catch { /* cookie may be blocked in some browsers */ }

    // 2) localStorage fallback (used by AppContext for Bearer auth on iOS Safari)
    try {
      localStorage.setItem('ua_session_token', token);
      localStorage.setItem('session_token', token);
    } catch { /* private mode */ }

    // 3) Redirect after a short pause so cookies settle
    const timer = setTimeout(() => setStatus('done'), 600);
    return () => clearTimeout(timer);
  }, [location.search]);

  if (status === 'done') {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6"
      style={{ background: '#FDFBF7', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <div className="max-w-sm w-full text-center"
        style={{
          background: '#FFFFFF',
          border: '1px solid rgba(45,35,35,0.08)',
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 4px 24px rgba(127,29,29,0.06)',
        }}>
        {status === 'working' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{ background: 'rgba(127,29,29,0.08)' }}>
              <Loader className="w-7 h-7 animate-spin" style={{ color: '#7F1D1D' }} />
            </div>
            <h1 className="text-xl font-bold mb-2" style={{ color: '#2D2323', fontFamily: 'Cabinet Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              Authenticating reviewer session...
            </h1>
            <p className="text-sm" style={{ color: '#736B6B' }}>
              Tugas: setup read-only access untuk akaun audit.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2 text-xs" style={{ color: '#4A7C59' }}>
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Session disahkan secara tempatan · tiada perubahan data dibenarkan</span>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{ background: '#FAECE9' }}>
              <AlertCircle className="w-7 h-7" style={{ color: '#C84B31' }} />
            </div>
            <h1 className="text-xl font-bold mb-2" style={{ color: '#2D2323', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Token tidak sah
            </h1>
            <p className="text-sm mb-4" style={{ color: '#736B6B' }}>
              URL ini perlukan parameter <code>?t=&lt;TOKEN&gt;</code>. Sila semak link asal yang dikongsi.
            </p>
            <a href="/login" data-testid="auto-login-fallback"
              className="inline-block px-5 py-2.5 rounded-full text-sm font-semibold"
              style={{ background: '#7F1D1D', color: '#FFFFFF' }}>
              Pergi ke halaman login biasa
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default AutoLoginPage;
